import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Star } from 'lucide-react'

import { Glow } from '@/components/generals/glow'
import { Reveal } from '@/components/generals/reveal'
import { Button } from '@/components/generals/button'
import { AnimatedHeading } from '@/components/generals/animated-heading'
import { HEADING_XL } from '@/components/generals/section-heading'
import { LogoStrip } from '@/components/sections/logo-strip'

const avatars = [
    '/images/avatar-1.png',
    '/images/avatar-2.png',
    '/images/avatar-3.png',
    '/images/avatar-4.png',
    '/images/avatar-5.png'
]

export const Hero: FC = (): ReactNode => {
    return (
        <>
            {/* The section's overflow is load-bearing: it slices the glow's
                lower half off, which is what gives the bloom its flat bottom. */}
            <section
                id='hero'
                className='relative w-full overflow-hidden px-6 pt-[120px] pb-[180px] md:px-8 lg:px-16 lg:pt-[160px] lg:pb-[250px]'
            >
                <Glow variant='hero' />

                <div className='relative z-1 mx-auto flex w-full max-w-[1200px] flex-col items-center gap-6 lg:gap-4'>
                    <div className='flex w-full flex-col items-center gap-8'>
                        <Reveal
                            immediate
                            delay={0.15}
                            y={16}
                            blur={8}
                            duration={0.8}
                            className='flex items-center gap-3'
                        >
                            <div className='flex items-center'>
                                {avatars.map((avatar, index) => (
                                    <span
                                        key={avatar}
                                        className='relative size-10 shrink-0 overflow-hidden rounded-full ring-1 ring-white/10'
                                        style={{ marginLeft: index === 0 ? 0 : -14, zIndex: avatars.length - index }}
                                    >
                                        <Image
                                            src={avatar}
                                            alt=''
                                            fill
                                            sizes='40px'
                                            className='object-cover'
                                        />
                                    </span>
                                ))}
                            </div>

                            <div className='flex flex-col gap-1'>
                                <div
                                    className='flex items-center gap-0.5'
                                    aria-label='Rated five out of five'
                                >
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <Star
                                            key={index}
                                            aria-hidden='true'
                                            className='size-3.5 fill-white text-white'
                                        />
                                    ))}
                                </div>

                                <p className='text-sm leading-none tracking-[-0.01em] text-white/56'>
                                    115+ happy clients
                                </p>
                            </div>
                        </Reveal>

                        <AnimatedHeading
                            as='h1'
                            immediate
                            delay={0.25}
                            segments={[
                                { text: 'Ready to' },
                                { text: 'scale', serif: true },
                                { text: 'your brand with paid ads?' }
                            ]}
                            className={`${HEADING_XL} mx-auto max-w-[16ch] text-center`}
                        />

                        <Reveal
                            immediate
                            delay={0.55}
                            y={16}
                            blur={8}
                            duration={0.8}
                            className='mx-auto max-w-[52ch] text-center text-[clamp(1rem,1.2vw,1.125rem)] leading-[1.55] tracking-[-0.01em] text-white/56'
                        >
                            If you want to achieve ground-breaking growth with increased sales and profitability with paid ads, then you&rsquo;re in the right place.
                        </Reveal>
                    </div>

                    <Reveal
                        immediate
                        delay={0.68}
                        y={16}
                        blur={8}
                        duration={0.8}
                        stagger={0.08}
                        className='mt-2 flex flex-wrap items-center justify-center gap-3'
                    >
                        <Button href='/contact'>
                            Book a call
                        </Button>

                        <Button
                            href='#clients'
                            variant='secondary'
                        >
                            Learn More
                        </Button>
                    </Reveal>
                </div>
            </section>

            <LogoStrip
                immediate
                delay={1}
            />
        </>
    )
}
